import { AxiosResponse } from 'axios';

import client from '../../lib/service/plan.service';
import {
  Benefit,
  PlanBenefitsResponse,
  PlanBenefitsParams,
  PlanBenefitResponse,
  PlanBenefitEdit,
  PlanBenefitData,
  BenefitCategory,
  BenefitCategoriesResponse,
  BenefitDetails,
  BenefitDetailsResponse,
  BenefitItemDetailsData,
  BenefitConsentData,
  BenefitConsent,
  BenefitConsentResponse,
  BenefitConsentsResponse,
  BenefitConsents,
  BenefitUpdateConsentData,
  BenefitConsentIdResponse,
  BenefitUpdateItemDetailsData,
  BenefitItemDetailsResponse,
  BenefitItemDetails,
} from './plan-benefit.types';

const PLAN_BENEFITS = 'v1/benefits';
const PLAN_BENEFIT_DETAILS = 'v1/benefit_details';
const PLAN_BENEFIT = (id: number) => `${PLAN_BENEFITS}/${id}`;
const PLAN_BENEFIT_CATEGORIES = 'v1/benefit_categories';
const PLAN_BENEFIT_CONSENTS = 'v1/benefit_consents';
const PLAN_DELETE_BENEFIT_CONSENT = (id: number) =>
  `${PLAN_BENEFIT_CONSENTS}/${id}`;

const PLAN_BENEFIT_ID_CONSENTS = (benefit_id: number) =>
  `/v1/benefits/${benefit_id}/benefit_consents`;
const PLAN_BENEFIT_CONSENT_ID = (id: number) =>
  `/${PLAN_BENEFIT_CONSENTS}/${id}`;
const PLAN_BENEFIT_ITEM_DETAILS_ID = (id: number) =>
  `/${PLAN_BENEFIT_DETAILS}/${id}`;
const PLAN_BENEFIT_ID_ITEM_DETAILS = (benefit_id: number) =>
  `/v1/benefits/${benefit_id}/benefit_details`;

const PLAN_DELETE_BENEFIT_ITEM_DETAILS = (id: number) =>
  `${PLAN_BENEFIT_DETAILS}/${id}`;

export const getPaginatedPlanBenefits = async (
  params: PlanBenefitsParams,
): Promise<PlanBenefitsResponse> => {
  const response = await client.get<
    string,
    AxiosResponse<PlanBenefitsResponse>
  >(PLAN_BENEFITS, { params });

  return response.data;
};

export const getPlanBenefit = async (id: number): Promise<Benefit> => {
  const response = await client.get<PlanBenefitResponse>(PLAN_BENEFIT(id));

  return response.data.benefit;
};

export const putPlanBenefit = async (
  data: PlanBenefitEdit,
): Promise<Benefit> => {
  const { id, ...rest } = data;

  const response = await client.put<PlanBenefitResponse>(PLAN_BENEFIT(id), {
    benefit: rest,
  });

  return response.data.benefit;
};

export const postPlanBenefit = async (
  data: PlanBenefitData,
): Promise<Benefit> => {
  const response = await client.post<PlanBenefitResponse>(PLAN_BENEFITS, {
    benefit: data,
  });

  return response.data.benefit;
};

export const getBenefitCategories = async (): Promise<BenefitCategory[]> => {
  const response = await client.get<
    string,
    AxiosResponse<BenefitCategoriesResponse>
  >(PLAN_BENEFIT_CATEGORIES);

  return response.data.benefit_categories;
};

export const postPlanBenefitItemDetails = async (
  data: BenefitItemDetailsData,
): Promise<BenefitDetails> => {
  const response = await client.post<BenefitDetailsResponse>(
    PLAN_BENEFIT_DETAILS,
    {
      benefit_detail: data,
    },
  );

  return response.data.benefit_detail;
};

export const postPlanBenefitConsent = async (
  data: BenefitConsentData,
): Promise<BenefitConsent> => {
  const response = await client.post<BenefitConsentResponse>(
    PLAN_BENEFIT_CONSENTS,
    {
      benefit_consent: data,
    },
  );

  return response.data.benefit_consent;
};

export const putPlanBenefitConsent = async (
  data: BenefitUpdateConsentData,
): Promise<BenefitConsent> => {
  const response = await client.put<BenefitConsentResponse>(
    PLAN_BENEFIT_CONSENT_ID(data?.id),
    {
      benefit_consent: data,
    },
  );

  return response.data.benefit_consent;
};

export const getPlanBenefitConsents = async (
  id: number,
): Promise<BenefitConsents[]> => {
  const response = await client.get<BenefitConsentsResponse>(
    PLAN_BENEFIT_ID_CONSENTS(id),
  );

  return response.data.benefit_consents;
};

export const getPlanBenefitConsentId = async (
  id: number,
): Promise<BenefitConsents> => {
  const response = await client.get<BenefitConsentIdResponse>(
    PLAN_BENEFIT_CONSENT_ID(id),
  );

  return response.data.benefit_consent;
};

export const deletePlanBenefitConsentId = async (id: number) => {
  await client.delete(PLAN_DELETE_BENEFIT_CONSENT(id));
};

export const deletePlanBenefitItemDetailsId = async (id: number) => {
  await client.delete(PLAN_DELETE_BENEFIT_ITEM_DETAILS(id));
};

export const getPlanBenefitIdItemDetails = async (
  id: number,
): Promise<BenefitItemDetails> => {
  const response = await client.get<BenefitItemDetailsResponse>(
    PLAN_BENEFIT_ID_ITEM_DETAILS(id),
  );

  return response.data.benefit_detail;
};

export const putPlanBenefitItemDetails = async (
  data: BenefitUpdateItemDetailsData,
): Promise<BenefitItemDetails> => {
  const response = await client.put<BenefitItemDetailsResponse>(
    PLAN_BENEFIT_ITEM_DETAILS_ID(data?.id),
    {
      benefit_detail: data,
    },
  );

  return response.data.benefit_detail;
};
